import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, Badge, urgencyBadge } from "@/components/Common";
import { Link } from "react-router-dom";
import FuelUploadNudge from "@/components/FuelUploadNudge";
import {
  TruckIcon,
  WrenchIcon,
  WarningIcon,
  CurrencyDollarIcon,
  CalendarCheckIcon,
  SteeringWheelIcon,
  GearIcon,
} from "@phosphor-icons/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const PIE_COLORS = ["#2563EB", "#16A34A", "#D97706", "#DC2626", "#0284C7", "#7C3AED", "#0F766E"];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([api.get("/dashboard/stats"), api.get("/alerts")]);
        setStats(s.data);
        setAlerts(a.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="p-12 font-mono text-sm text-slate-500" data-testid="dashboard-loading">
        LOADING METRICS...
      </div>
    );
  }

  const top_alerts = alerts.slice(0, 6);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Control Room"
        title="Fleet Dashboard"
        subtitle="Operational snapshot of your trucking fleet — repairs, services, and costs at a glance."
      />

      <div className="p-8 space-y-8">
        <FuelUploadNudge />

        {/* North star metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Trucks" value={stats.total_trucks} sub={`${stats.active_trucks} active`} icon={TruckIcon} testid="stat-total-trucks" />
          <StatCard label="Trailers" value={stats.total_trailers || 0} sub={`${stats.total_reefers || 0} reefers · ${stats.total_dry_vans || 0} dry vans`} icon={TruckIcon} testid="stat-total-trailers" />
          <StatCard label="Overdue Services" value={stats.overdue_services} sub={`${stats.upcoming_services} due in 14 days`} icon={WarningIcon} tone={stats.overdue_services > 0 ? "danger" : "default"} testid="stat-overdue" />
          <StatCard label="Total Repair Cost" value={`$${Number(stats.total_repair_cost).toLocaleString()}`} sub={`${stats.total_repairs} repair records`} icon={CurrencyDollarIcon} tone="info" testid="stat-total-cost" />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-slate-300 rounded-sm" data-testid="cost-by-month-chart">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="section-eyebrow mb-0.5">/// Trend</div>
                <h3 className="font-display text-base font-bold tracking-tight">Repair cost by month</h3>
              </div>
              <Badge tone="info">Last 6 mo.</Badge>
            </div>
            <div className="p-4 h-72">
              {stats.cost_by_month.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-slate-500 font-mono">No data yet</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.cost_by_month}>
                    <CartesianGrid stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#64748B" }} />
                    <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#64748B" }} />
                    <Tooltip
                      contentStyle={{ borderRadius: 2, border: "1px solid #CBD5E1", fontSize: 12, fontFamily: "IBM Plex Mono" }}
                      formatter={(v) => [`$${Number(v).toLocaleString()}`, "Cost"]}
                    />
                    <Bar dataKey="cost" fill="#2563EB" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-300 rounded-sm" data-testid="cost-by-category-chart">
            <div className="px-5 py-3 border-b border-slate-200">
              <div className="section-eyebrow mb-0.5">/// Breakdown</div>
              <h3 className="font-display text-base font-bold tracking-tight">Cost by category</h3>
            </div>
            <div className="p-4 h-72">
              {stats.cost_by_category.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-slate-500 font-mono">No data yet</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={stats.cost_by_category} dataKey="cost" nameKey="category" outerRadius={80} label={{ fontSize: 10 }}>
                      {stats.cost_by_category.map((entry, i) => (
                        <Cell key={`cell-${entry.category}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Alerts panel */}
        <div className="bg-white border border-slate-300 rounded-sm" data-testid="alerts-panel">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="section-eyebrow mb-0.5">/// Service queue</div>
              <h3 className="font-display text-base font-bold tracking-tight">Upcoming & overdue services</h3>
            </div>
            <Link to="/alerts" className="text-sm text-blue-600 hover:underline font-semibold" data-testid="view-all-alerts">
              View all →
            </Link>
          </div>

          {top_alerts.length === 0 ? (
            <div className="p-8 text-center">
              <CalendarCheckIcon size={32} weight="thin" className="mx-auto text-slate-400 mb-2" />
              <div className="text-sm text-slate-500">No scheduled services. All systems nominal.</div>
            </div>
          ) : (
            <table className="fleet-table">
              <thead>
                <tr>
                  <th>Urgency</th>
                  <th>Truck</th>
                  <th>Service</th>
                  <th>Next due</th>
                  <th>Days / Miles</th>
                </tr>
              </thead>
              <tbody>
                {top_alerts.map((a) => {
                  const u = urgencyBadge(a.urgency);
                  return (
                    <tr key={a.id} data-testid={`alert-row-${a.id}`}>
                      <td><Badge tone={u.tone}>{u.label}</Badge></td>
                      <td className="font-mono font-semibold">{a.truck_unit} · {a.truck_label}</td>
                      <td className="capitalize">{a.service_type.replace(/_/g, " ")}</td>
                      <td className="font-mono">{a.next_due_date?.slice(0,10) || "—"}</td>
                      <td className="font-mono">
                        {a.days_until !== null && a.days_until !== undefined ? (
                          <span className={a.days_until < 0 ? "text-red-700 font-semibold" : ""}>
                            {a.days_until < 0 ? `${Math.abs(a.days_until)}d overdue` : `${a.days_until}d left`}
                          </span>
                        ) : "—"}
                        {a.mileage_remaining !== null && a.mileage_remaining !== undefined && (
                          <span className="ml-2 text-slate-500">/ {a.mileage_remaining.toLocaleString()} mi</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
